import { Rng } from '../core/rng';
import { clamp, Vec3 } from '../core/math';
import { halfBreadthXY, deckHalfWidth } from '../data/ship';
import { sectionAt } from './damage';
import type { SimEvents } from './events';
import type { Vessel } from './vessel';

// Ships touching: collisions (and deliberate ramming) and grappling alongside for a boarding fight.

const STATIONS = [-14, -9, -4, 1, 6, 11, 15.5];

interface Sphere {
  x: number;
  y: number;
  z: number;
  r: number;
  xd: number;
}

function hullSpheres(v: Vessel, out: Sphere[]): Sphere[] {
  out.length = 0;
  const s = v.ship;
  for (const xd of STATIONS) {
    const hb = Math.max(0.8, halfBreadthXY(xd, 0.3));
    const p = s.designToWorld(tmp.set(xd, 0.4, 0), tmp);
    out.push({ x: p.x, y: p.y, z: p.z, r: hb * s.sz, xd });
  }
  return out;
}

export class Contacts {
  private readonly rng: Rng;
  private readonly sa: Sphere[] = [];
  private readonly sb: Sphere[] = [];
  private fightTimer = 0;

  constructor(seed: number) {
    this.rng = new Rng(seed);
  }

  update(dt: number, vessels: readonly Vessel[], events: SimEvents, wantBoard: (a: Vessel, b: Vessel) => boolean): void {
    for (let i = 0; i < vessels.length; i++) {
      const a = vessels[i]!;
      if (!a.alive) continue;
      for (let j = i + 1; j < vessels.length; j++) {
        const b = vessels[j]!;
        if (!b.alive) continue;
        const reach = 22 * (a.ship.sx + b.ship.sx) + 10;
        if (Math.abs(a.ship.com.x - b.ship.com.x) > reach || Math.abs(a.ship.com.z - b.ship.com.z) > reach) continue;
        this.collide(dt, a, b, events);
        this.grapple(dt, a, b, events, wantBoard);
      }
    }
    this.fightTimer -= dt;
    if (this.fightTimer <= 0) {
      this.fightTimer = 1;
      for (const a of vessels) {
        if (a.boardingWith < 0 || !a.alive) continue;
        const b = vessels.find((v) => v.id === a.boardingWith);
        if (b && a.id < b.id) this.melee(a, b, events);
      }
    }
  }

  private collide(dt: number, a: Vessel, b: Vessel, events: SimEvents): void {
    hullSpheres(a, this.sa);
    hullSpheres(b, this.sb);
    for (const p of this.sa) {
      for (const q of this.sb) {
        const dx = p.x - q.x, dz = p.z - q.z;
        const d = Math.hypot(dx, dz);
        const pen = p.r + q.r - d;
        if (pen <= 0 || d < 1e-3) continue;
        const nx = dx / d, nz = dz / d;
        const cx = (p.x + q.x) / 2, cy = (p.y + q.y) / 2, cz = (p.z + q.z) / 2;
        const va = a.ship.pointVelocity(a.ship.worldToLocal(tmp.set(cx, cy, cz), tmp), tmpVa);
        const vb = b.ship.pointVelocity(b.ship.worldToLocal(tmp.set(cx, cy, cz), tmp), tmpVb);
        const vn = (va.x - vb.x) * nx + (va.z - vb.z) * nz;
        const ma = a.ship.mass, mb = b.ship.mass;
        const mu = (ma * mb) / (ma + mb);
        // Stiff springs keep hulls apart; an impulse kills the closing speed.
        const k = mu * 8;
        a.ship.addForce(nx * pen * k, 0, nz * pen * k, cx, cy, cz);
        b.ship.addForce(-nx * pen * k, 0, -nz * pen * k, cx, cy, cz);
        if (vn < -0.3) {
          const j = -1.1 * vn * mu * 0.5;
          a.ship.applyImpulse(nx * j, 0, nz * j, cx, cy, cz);
          b.ship.applyImpulse(-nx * j, 0, -nz * j, cx, cy, cz);
          // Timbers crush: the side struck suffers far more than a stout stem.
          const energy = 0.5 * mu * vn * vn;
          if (energy > 4e4) {
            const la = a.ship.localToDesign(a.ship.worldToLocal(tmp.set(cx, cy, cz), tmp), tmpD);
            const lb = b.ship.localToDesign(b.ship.worldToLocal(tmp.set(cx, cy, cz), tmp), tmpE);
            const aBow = la.x > 12 ? 0.35 : 1;
            const bBow = lb.x > 12 ? 0.35 : 1;
            const dmg = energy / 2.5e4;
            a.damage.damageHull(sectionAt(la.x), Math.sign(la.z) || 1, dmg * aBow / a.cls.hull, events, { x: cx, y: cy, z: cz });
            b.damage.damageHull(sectionAt(lb.x), Math.sign(lb.z) || 1, dmg * bBow / b.cls.hull, events, { x: cx, y: cy, z: cz });
            if (energy > 4e5) {
              if (aBow > 0.5) a.damage.addHole(la.x, 0.2, Math.sign(la.z) * halfBreadthXY(la.x, 0.2), clamp(Math.sqrt(energy) / 2500, 0.2, 1.2));
              if (bBow > 0.5) b.damage.addHole(lb.x, 0.2, Math.sign(lb.z) * halfBreadthXY(lb.x, 0.2), clamp(Math.sqrt(energy) / 2500, 0.2, 1.2));
            }
            events.push({ type: 'collision', x: cx, y: cy + 1, z: cz, energy: clamp(energy / 1e6, 0.05, 1) });
            a.crew.strike(la.x, 3, la.z, 4, 0.12, a.hitCtx(events));
            b.crew.strike(lb.x, 3, lb.z, 4, 0.12, b.hitCtx(events));
          }
        }
      }
    }
    void dt;
  }

  /** Grapnels across when a boarder is alongside and slow enough. */
  private grapple(dt: number, a: Vessel, b: Vessel, events: SimEvents, wantBoard: (a: Vessel, b: Vessel) => boolean): void {
    const d = Math.hypot(a.ship.com.x - b.ship.com.x, a.ship.com.z - b.ship.com.z);
    const beams = 5.5 * (a.ship.sz + b.ship.sz);
    const linked = a.boardingWith === b.id;
    if (!linked) {
      if (!a.fighting && !b.fighting) return;
      if (d > beams + 7) return;
      const rel = Math.hypot(a.ship.vel.x - b.ship.vel.x, a.ship.vel.z - b.ship.vel.z);
      if (rel > 3 || !(wantBoard(a, b) || wantBoard(b, a))) return;
      a.boardingWith = b.id;
      b.boardingWith = a.id;
      events.push({ type: 'boarding', a: a.id, b: b.id, x: (a.ship.com.x + b.ship.com.x) / 2, z: (a.ship.com.z + b.ship.com.z) / 2 });
    }
    // Grappled: hauled together and made fast.
    const la = a.ship.worldToLocal(b.ship.com, tmp);
    a.boardingSide = la.z > 0 ? 1 : -1;
    const lb = b.ship.worldToLocal(a.ship.com, tmpD);
    b.boardingSide = lb.z > 0 ? 1 : -1;
    const nx = (b.ship.com.x - a.ship.com.x) / (d || 1), nz = (b.ship.com.z - a.ship.com.z) / (d || 1);
    const want = beams + 1.5;
    const mu = (a.ship.mass * b.ship.mass) / (a.ship.mass + b.ship.mass);
    const f = (d - want) * mu * 0.4;
    const dvx = b.ship.vel.x - a.ship.vel.x, dvz = b.ship.vel.z - a.ship.vel.z;
    a.ship.addForce(nx * f + dvx * mu * 0.3, 0, nz * f + dvz * mu * 0.3, a.ship.com.x, a.ship.com.y, a.ship.com.z);
    b.ship.addForce(-nx * f - dvx * mu * 0.3, 0, -nz * f - dvz * mu * 0.3, b.ship.com.x, b.ship.com.y, b.ship.com.z);
    // Lines part if the ships are torn apart or one of them is going down.
    if (d > beams + 25 || a.state === 'sinking' || b.state === 'sinking') {
      a.boardingWith = -1;
      b.boardingWith = -1;
    }
    void dt;
  }

  /** A round of hand-to-hand fighting across the rails. */
  private melee(a: Vessel, b: Vessel, events: SimEvents): void {
    if (!a.fighting && !b.fighting) {
      a.boardingWith = b.boardingWith = -1;
      return;
    }
    const sa = a.crew.fighters * (0.4 + a.crew.morale) * (0.6 + 0.4 * a.crew.gunnery);
    const sb = b.crew.fighters * (0.4 + b.crew.morale) * (0.6 + 0.4 * b.crew.gunnery);
    const total = sa + sb + 1e-3;
    const lossA = Math.round(this.rng.range(0, 2.5) * (sb / total) * Math.sqrt(Math.max(1, b.crew.fighters) / 8));
    const lossB = Math.round(this.rng.range(0, 2.5) * (sa / total) * Math.sqrt(Math.max(1, a.crew.fighters) / 8));
    this.casualties(a, lossA, events);
    this.casualties(b, lossB, events);
    a.crew.morale = clamp(a.crew.morale + (sa > sb ? 0.01 : -0.02), 0, 1);
    b.crew.morale = clamp(b.crew.morale + (sb > sa ? 0.01 : -0.02), 0, 1);
    for (const [loser, winner, s1, s2] of [[a, b, sa, sb], [b, a, sb, sa]] as const) {
      if ((loser.crew.effective < winner.crew.effective * 0.2 || loser.crew.morale < 0.08 || s1 < s2 * 0.15) && loser.fighting) {
        loser.state = 'captured';
        loser.ship.sails.struck = true;
        loser.ship.sails.setPlan(0);
        loser.battle = false;
        events.push({ type: 'captured', vessel: loser.id, by: winner.id });
        a.boardingWith = b.boardingWith = -1;
      }
    }
  }

  private casualties(v: Vessel, n: number, events: SimEvents): void {
    for (let k = 0; k < n; k++) {
      // Those at the rail go first; then the men who come up to replace them.
      const front = v.crew.members.filter((m) => m.alive && m.onBoard && m.health > 0.4 && Math.abs(m.z) > deckHalfWidth(m.x) - 1.2);
      const m = front.length ? front[this.rng.int(front.length)]! : null;
      if (m) v.crew.injure(m, this.rng.range(0.6, 1.4), v.hitCtx(events));
      else if (v.crew.reserve > 0) v.crew.reserve--;
    }
  }
}

const tmp = new Vec3(), tmpD = new Vec3(), tmpE = new Vec3(), tmpVa = new Vec3(), tmpVb = new Vec3();
